import { createElement, useRef, type ComponentType } from 'react';
import type { ObservableInput } from 'rxjs';
import { connect } from '@ardoq/rxbeach';

/**
 * Connects a component to a stream built from the props it mounts with. The
 * stream is created once per mount; later props are passed through to the
 * component without recreating it. The props are passed to the component as
 * well, alongside the values from the stream.
 */
export const connectInstanceWithProps = <
  Props extends object,
  Observed extends Partial<Props>,
  InstanceProps extends object,
>(
  Component: ComponentType<Props>,
  createInstanceStream: (
    instanceProps: InstanceProps
  ) => ObservableInput<Observed>
) =>
  function ConnectInstanceWithProps(
    props: InstanceProps & Omit<Props, keyof Observed>
  ) {
    const connectedRef = useRef<ComponentType<
      Omit<Props, keyof Observed>
    > | null>(null);
    if (!connectedRef.current) {
      connectedRef.current = connect(Component, createInstanceStream(props));
    }
    const componentProps: Omit<Props, keyof Observed> = props;

    return createElement(connectedRef.current, componentProps);
  };
